import { useCallback, useRef, useState } from "react";
import { NarAgentGridService } from "../../../../../api/services/narAgentGrid/narAgentGridService";
import {
  KillSwitchBulkRequest,
  KillSwitchBulkResponse,
} from "../../../../../api/services/narAgentGrid/types";

interface UseNarAgentGridBulkToggleOptions {
  /** Called after the bulk kill-switch request succeeds. */
  onSuccess?: (response: KillSwitchBulkResponse) => void;
  /** Called when the bulk kill-switch request fails. */
  onError?: (error: string) => void;
}

/**
 * Manages the NAR Agent Grid bulk kill-switch API call with loading and error
 * state, plus optional success/error callbacks.
 */
export const useNarAgentGridBulkToggle = (
  options: UseNarAgentGridBulkToggleOptions = {},
) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const callbacksRef = useRef(options);
  callbacksRef.current = options;

  const bulkToggle = useCallback(
    async (payload: KillSwitchBulkRequest) => {
      if (loading) {
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const response =
          await NarAgentGridService.killSwitchBulkToggle(payload);
        setLoading(false);
        callbacksRef.current.onSuccess?.(response);
        return response;
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        setError(message);
        setLoading(false);
        callbacksRef.current.onError?.(message);
      }
    },
    [loading],
  );

  const resetError = useCallback(() => {
    setError(null);
  }, []);

  return {
    loading,
    error,
    bulkToggle,
    resetError,
  };
};
